import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Home, FolderOpen, Search, Library, Users, BarChart3, LogOut,
  Receipt, Briefcase, Shield,
} from 'lucide-react';
import { isAdminLike } from '../lib/roles';

/**
 * AppSidebar — shared sidebar for invoicing & clients pages.
 *
 * `active` prop is the nav key to highlight: home | documents | research |
 * library | admin | usage | clients | invoices.
 */
const AppSidebar = ({ user, onLogout, active }) => {
  const navigate = useNavigate();
  const isAdmin = isAdminLike(user);

  const itemClass = (key) => {
    const base = 'flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all';
    if (active === key) {
      return `${base} bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white shadow-lg`;
    }
    return `${base} text-slate-300 hover:bg-white/5 hover:text-white`;
  };

  return (
    <aside className="w-72 bg-gradient-to-b from-[#0F172A] to-[#1E293B] min-h-screen fixed left-0 top-0 flex flex-col">
      {/* Logo Header */}
      <div className="p-6 border-b border-white/10">
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
          data-testid="nav-home"
        >
          <div className="w-10 h-10 relative rounded-lg overflow-hidden bg-white flex-shrink-0">
            <img src="/aks-logo.png" alt="AKS" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-white leading-tight">AKS TAX AND LEGAL<br/>CONSULTANCY</h1>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">Menu</p>
        <ul className="space-y-1">
          <li>
            <button onClick={() => navigate('/')} className={itemClass('home')} data-testid="nav-home-btn">
              <Home className="w-5 h-5" /> Home
            </button>
          </li>
          <li>
            <button onClick={() => navigate('/dashboard')} className={itemClass('documents')} data-testid="nav-documents">
              <FolderOpen className="w-5 h-5" /> {isAdmin ? 'All Notices' : 'My Notices'}
            </button>
          </li>
          <li>
            <button onClick={() => navigate('/research')} className={itemClass('research')} data-testid="nav-research">
              <Search className="w-5 h-5" /> Legal Research
            </button>
          </li>
          <li>
            <button onClick={() => navigate('/library')} className={itemClass('library')} data-testid="nav-library">
              <Library className="w-5 h-5" /> Legal Library
            </button>
          </li>
          {isAdmin && (
            <>
              <li>
                <button onClick={() => navigate('/admin')} className={itemClass('admin')} data-testid="nav-admin">
                  <Users className="w-5 h-5" /> User Management
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/admin/usage')} className={itemClass('usage')} data-testid="nav-usage">
                  <BarChart3 className="w-5 h-5" /> Usage &amp; Costs
                </button>
              </li>
            </>
          )}
        </ul>

        {isAdmin && (
          <>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 mt-6 px-3">Billing</p>
            <ul className="space-y-1">
              <li>
                <button onClick={() => navigate('/clients')} className={itemClass('clients')} data-testid="nav-clients">
                  <Briefcase className="w-5 h-5" /> Clients
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/invoices')} className={itemClass('invoices')} data-testid="nav-invoices">
                  <Receipt className="w-5 h-5" /> Invoices
                </button>
              </li>
            </ul>
          </>
        )}

        <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-sm font-medium text-white">Secure Portal</span>
          </div>
          <p className="text-xs text-slate-400">
            All documents and invoices are encrypted in transit and at rest.
          </p>
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4 p-3 bg-white/5 rounded-xl">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full ring-2 ring-[#F59E0B]" />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center text-white font-medium ring-2 ring-[#F59E0B]">
              {user?.name?.charAt(0) || (isAdmin ? 'A' : 'U')}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-[#F59E0B] font-medium">
              {user?.role === 'super_admin' ? 'Super Admin' : isAdmin ? 'Administrator' : 'Client'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
};

export default AppSidebar;
